import { useEffect, useState, useTransition } from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  convertDateToLocaleString,
  convertLocaleDateToUTCString,
  IDateReturn,
  setToUTCMidnight,
  stringDateCheck,
} from "../utility";
import { AsyncButton } from "./AsyncButton";
import { InTextLink } from "./InTextLink";

/**
 * Props for the ControlDate component.
 */
export interface ControlDateProps {
  id: string;
  label: string;
  labelWidth?: number;
  maxWidthCols?: number;
  value: string | Date;
  setValue?: (ret: string) => void;
  disabled?: boolean;
  timeAllowed?: boolean;
  pastBad?: boolean;
  futureBad?: boolean;
  blankBad?: boolean;
  showNowButton?: boolean;
}

/**
 * A date and time picker control that supports both date-only and date-time selection with validation options.
 * Features include locale-aware date formatting, UTC conversion, configurable validation rules for past/future dates,
 * and an optional "Now" button for setting current date/time.
 *
 * @param props - Component props.
 * @param props.id - Unique identifier for the date control
 * @param props.blankBad - Whether blank/empty dates should be considered invalid
 * @param props.disabled - Whether the date picker is disabled
 * @param props.futureBad - Whether future dates should be considered invalid
 * @param props.label - Label text for the date field
 * @param props.labelWidth - Width of the label column in Bootstrap grid units (defaults to 2)
 * @param props.maxWidthCols - Maximum width in Bootstrap grid columns (defaults to 12)
 * @param props.pastBad - Whether past dates should be considered invalid
 * @param props.setValue - Callback function to update the date value
 * @param props.showNowButton - Whether to show the "Now" button for setting current date/time (defaults to false)
 * @param props.timeAllowed - Whether to include time selection
 * @param props.value - Current date value (ISO string format or Date object)
 * @returns A React functional component rendering a Form.Group with date/time inputs and optional "Now" button
 */
export const ControlDate = ({
  id,
  label,
  labelWidth = 2,
  maxWidthCols = 12,
  value,
  setValue,
  disabled,
  timeAllowed,
  pastBad,
  futureBad,
  blankBad,
  showNowButton = false,
}: ControlDateProps): React.ReactElement => {
  const [currentDate, setCurrentDate] = useState<string>("");
  const [currentTime, setCurrentTime] = useState<string>("");
  const [isNowActive, setIsNowActive] = useState<boolean>(false);
  const [, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      switch (value) {
        case "now":
          setIsNowActive(true);
          break;
        case "":
          setIsNowActive(false);
          setCurrentDate("");
          setCurrentTime("");
          break;
        default: {
          setIsNowActive(false);
          const d = convertDateToLocaleString(new Date(value));
          setCurrentDate(d.slice(0, 10));
          setCurrentTime(d.slice(11, 19));
          break;
        }
      }
    });
  }, [timeAllowed, value]);

  const validationReturn:
    IDateReturn | { success: true; returnedDate: string; errorText?: string } =
    currentDate === "" && blankBad
      ? { success: false, validationText: "Date missing" }
      : isNowActive
        ? { success: true, returnedDate: "now" }
        : stringDateCheck(`${currentDate} ${currentTime}`.trim(), {
            blankBad,
            pastBad,
            futureBad,
          });

  /**
   * Return the current date and time in UTC format or "now" via setValue callback.
   *
   * @param now - -Whether to set the value to "now"
   */
  const returnDate = (now?: boolean) => {
    if (now) setValue?.("now");
    else {
      const s = `${currentDate} ${currentTime}`.trim();
      const ret = s !== "" ? convertLocaleDateToUTCString(s) : "";
      if (ret !== value && ret !== "Invalid date") setValue?.(ret);
    }
  };

  return (
    <>
      <Form.Group as={Row}>
        <Form.Label
          column
          md={labelWidth}
        >
          {label}
        </Form.Label>
        <Col
          md={Math.floor((maxWidthCols - labelWidth) / 2)}
          className={"d-flex align-items-center"}
        >
          <Form.Control
            id={`${id}-date`}
            aria-label={`${label} date`}
            size="sm"
            value={isNowActive ? "" : currentDate}
            disabled={disabled || !setValue || isNowActive}
            type={"date"}
            min={pastBad ? new Date().getDate() : undefined}
            max={futureBad ? new Date().getDate() : undefined}
            onChange={
              setValue
                ? (e) => {
                    let newDate = "";
                    if (!timeAllowed && e.currentTarget.value !== "") {
                      const d = new Date(e.currentTarget.value);
                      /* istanbul ignore else */
                      if (!isNaN(d.getTime())) {
                        const m = setToUTCMidnight(d, d.getTimezoneOffset() > 0);
                        newDate = m.slice(0, 10);
                        setCurrentTime(m.slice(11, 19));
                      }
                    } else {
                      newDate = e.currentTarget.value;
                    }
                    setCurrentDate(newDate);
                  }
                : undefined
            }
            className={`${validationReturn.success === true ? "is-valid" : "is-invalid"}`}
            onBlur={
              setValue
                ? (e) => {
                    setCurrentDate(e.currentTarget.value);
                    returnDate();
                  }
                : undefined
            }
          />
        </Col>
        <Col
          md={Math.ceil((maxWidthCols - labelWidth) / 2)}
          className={"d-flex gap-4 align-items-center"}
        >
          <Form.Control
            id={`${id}-time`}
            aria-label={`${label} time`}
            size="sm"
            value={isNowActive ? "" : currentTime}
            disabled={disabled || !setValue || !timeAllowed || isNowActive}
            type={"time"}
            onChange={
              setValue
                ? (e) => {
                    setCurrentTime(e.currentTarget.value);
                    if (e.currentTarget.value === "") setCurrentDate("");
                  }
                : undefined
            }
            className={`${validationReturn.success === true ? "is-valid" : "is-invalid"}`}
            onBlur={
              setValue
                ? (e) => {
                    setCurrentTime(e.currentTarget.value);
                    returnDate();
                  }
                : undefined
            }
          />
          {showNowButton && (
            <AsyncButton
              id={`${id}-now-button`}
              size="sm"
              variant={isNowActive ? "success" : "outline-primary"}
              aria-label="Now"
              style={{ width: "72px" }}
              title="Set the date and time to now"
              onClick={() => {
                returnDate(!isNowActive);
              }}
            >
              Now
            </AsyncButton>
          )}
        </Col>
      </Form.Group>
      <Row className="mb-1">
        <Col md={{ offset: labelWidth }}>
          <small
            style={validationReturn.success === false ? { color: "var(--bs-danger)" } : undefined}
          >
            <InTextLink
              text={
                isNowActive
                  ? "Time is set when submitted"
                  : validationReturn.success === true
                    ? validationReturn.returnedDate
                      ? validationReturn.returnedDate.toString()
                      : ""
                    : (validationReturn.validationText ??
                      // istanbul ignore next
                      "")
              }
            />
          </small>
        </Col>
      </Row>
    </>
  );
};

ControlDate.displayName = "ControlDate";
